import { z } from 'zod'
import { oc } from '@orpc/contract'
import { BILLING_ENTITY_VALUES } from '../billing-entity.contract'
import { BILLING_STATUS_VALUES } from '../../care-coordination/dto/billing-status.contract'
import { billingCaseDtoSchema } from './update-case.contract'

// POST (filter object in body): the SQL-driven dashboard source — one endpoint
// replaces the legacy page's five-source stitching. paymentStatus / statuses of
// the aggregate stay plain strings (their value sets live in billing-v2-shared.ts,
// which must not cross the web package boundary); the payment-cell labels are
// derived on the FRONTEND from paymentStatus + payments — this endpoint ships
// facts, not labels.

export const MAX_RANGE_MONTHS = 12

export const listCasesInputSchema = z
  .object({
    dateFrom: z.iso.datetime(),
    dateTo: z.iso.datetime(),
    patientId: z.string().optional(),
    claimStatus: z.array(z.enum(BILLING_STATUS_VALUES)).optional(),
    paymentStatus: z.array(z.string()).optional(),
    submissionRoute: z.array(z.string()).optional(),
    billingEntity: z.array(z.enum(BILLING_ENTITY_VALUES)).optional(),
    /** FHIR Appointment.status values. */
    appointmentStatus: z.array(z.string()).optional(),
    /** FHIR Encounter.status values (the progress-note-status mapping legacy sends). */
    encounterStatus: z.array(z.string()).optional(),
    /** submit_last_error IS NOT NULL — the Bridge TRIAGE/blocked-claim alarm. */
    needsAttention: z.boolean().optional(),
    count: z.number().int().min(1).max(100).default(50),
    offset: z.number().int().min(0).default(0),
  })
  .superRefine((value, ctx) => {
    const from = new Date(value.dateFrom)
    const to = new Date(value.dateTo)
    if (to < from) {
      ctx.addIssue({
        code: 'custom',
        path: ['dateTo'],
        message: 'dateTo must not be before dateFrom',
      })
      return
    }
    const max = new Date(from)
    max.setUTCMonth(max.getUTCMonth() + MAX_RANGE_MONTHS)
    if (to > max) {
      ctx.addIssue({
        code: 'custom',
        path: ['dateTo'],
        message: `Date range must not exceed ${MAX_RANGE_MONTHS} months`,
      })
    }
  })

export const casePaymentsSchema = z.object({
  copayPaidCents: z.number().nullable(),
  insurancePaidCents: z.number(),
  lastPaymentStatus: z.string().nullable(),
})

// The attached Coverage's insurance verdict (model/eligibility; values inlined — contract files
// stay browser-safe, see list-appointment-agenda.contract.ts).
export const caseInsuranceStatusSchema = z.object({
  /** Payer eligibility of the attached Coverage. */
  payerEligibility: z.enum([
    'verified',
    'approved-manually',
    'disapproved',
    'not-verified',
    'none',
  ]),
  /** The case org's clinic decision on that Coverage. */
  clinicDecision: z.enum(['accepted', 'not-accepted', 'none']),
  /** Roll-up: payer eligible and the clinic did not decline → verified. */
  insuranceStatus: z.enum(['verified', 'review-required', 'none']),
  /**
   * @deprecated Approximated from `payerEligibility` for web builds that still render it;
   * remove once none do.
   */
  eligibility: z.enum(['active', 'not-active', 'unknown']),
})

export const caseDisplaySchema = z.object({
  patientName: z.string(),
  patientDob: z.string().optional(),
  /** formatAddress output; '\n' separates the street line from the rest. */
  patientAddress: z.string().optional(),
  practitionerName: z.string().optional(),
  insuranceName: z.string().optional(),
  planName: z.string().optional(),
  memberId: z.string().optional(),
  /** The legacy billing-status updated-at extension (date under the Claim Status pill). */
  claimStatusUpdatedAt: z.string().optional(),
  /** Present only when the appointment carries an attached Coverage. */
  insuranceStatus: caseInsuranceStatusSchema.optional(),
  /** Where the self-pay fee stands; present only on a self-pay visit. */
  selfPayStatus: z.enum(['unpaid', 'paid', 'refunded']).optional(),
})

export const listCaseItemSchema = billingCaseDtoSchema.extend({
  payments: casePaymentsSchema,
  display: caseDisplaySchema,
})

export type BillingListCaseItem = z.infer<typeof listCaseItemSchema>

export const billingV2ListCases = oc
  .route({ method: 'POST', path: '/v2/billing/list' })
  .input(listCasesInputSchema)
  .output(z.object({ total: z.number(), items: z.array(listCaseItemSchema) }))
